import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, Output } from "@angular/core";
import * as d3 from "d3";
import dagre from "dagre-d3";
import _ from "lodash";
import { ICourse, ICourseLink, ISchool, IVideo, SchoolsService } from "src/app/services";

const ORIENTATION = { horizontal: "LR", vertical: "TB" };

@Component({
  selector: "app-school-chart",
  templateUrl: "./school-chart.component.html",
  styleUrls: ["./school-chart.component.scss"],
})
export class SchoolChartComponent implements AfterViewInit {
  private graph;
  private svg;
  private renderer;

  public zoomMin: number = 0.1;
  public zoomMax: number = 10;

  @Input()
  public school: ISchool;
  private _orientation;
  @Input()
  public set orientation(value: string) {
    this._orientation = value || "horizontal";
    if (this.graph) {
      this.zoomLevel = 1
      this.update();
      // reset zoom and translate due to the issue with nodes position calculation
      // not considering the current scale
      this.zoom.translateTo(this.svg, 0, 0, [0, 0]);
    }
  }

  @Output()
  public onCourseSelected = new EventEmitter<ICourse>();

  private zoom: any;
  private _zoomLevel: number;
  public get zoomLevel(): number {
    return this._zoomLevel;
  }
  public set zoomLevel(value: number) {
    if (this.zoomLevel === value) {
      return;
    }
    this._zoomLevel = value;
    this.zoom.scaleTo(this.svg, value);
  }

  constructor(
    private readonly schoolsService: SchoolsService,
    private elRef: ElementRef,
  ) {
  }

  @HostListener("document:click", ["$event"])
  onNodeClick(event) {
    const el = event.target;
    const node = el.closest(".node");
    if (this.elRef.nativeElement.contains(el) && node) {
      this.onCourseSelected.next(_.find(this.school.courses, { id: +node.__data__ }));
    }
  }

  ngAfterViewInit(): void {
    this.graph = new dagre.graphlib.Graph().setGraph({});
    _.each(this.school.courses, (course) => {
      this.graph.setNode(course.id);
    });
    _.each(this.school.coursesLinks, (link) => {
      this.graph.setEdge(link.parentId, link.childId, { label: " " });
    });
    this.svg = d3.select(this.elRef.nativeElement);

    this.zoom = d3.zoom().scaleExtent([this.zoomMin, this.zoomMax]).on("zoom", () => {
      this.svg.select("g").attr("transform", d3.event.transform);
      this.zoomLevel = d3.event.transform.k;
    });

    this.svg.call(this.zoom);

    // Create the renderer
    this.renderer = new dagre.render();
    this.update();
  }

  private update() {
    const options = this.graph.graph();
    options.rankdir = ORIENTATION[this._orientation];
    options.ranksep = this._orientation === "horizontal" ? 200 : 100;

    // Set html for nodes
    _.each(this.graph.nodes(), (id) => {
      const course = _.find(this.school.courses, { id: +id });
      this.graph.setNode(id, {
        labelType: "html",
        label: this.courseTemplate(course),
        rx: 5,
        ry: 5,
        padding: 0,
      });
    });

    this.renderer(this.svg.select("g"), this.graph);
    this.svg.attr("height", this.graph.graph().height + 40);
  }

  private courseTemplate(d: ICourse) {
    return `<div class="course course-${d.id}">
      <div class="name">${d.name}</div>
      <div class="units">${d.units} units</div>
      <div class="info">${d.additionalInfo}</div>
      ${this.videosTemplate(d.videos)}
     </div>`;
  }

  private videosTemplate(videos: IVideo[]) {
    if (!videos) return "";
    return `<div class="videos">
      ${_.map(videos, (video: IVideo) => this.videoTemplate(video)).join("")}
    </div>`;
  }

  private videoTemplate(video: IVideo) {
    return `<a class="video" href="${video.videoUrl}"}>
      <i class="ion-md-play-circle"></i>
      <img src="${video.thumbnailUrl}"/>
    </a>`;
  }
}
